<template>
  <AuthLayout
    title="Nouveau mot de passe"
    subtitle="Choisissez un nouveau mot de passe sécurisé"
    show-back-link
  >
    <!-- Invalid Token -->
    <div v-if="!token" class="space-y-6">
      <div class="p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <div>
            <p class="font-medium text-error-800 dark:text-error-200">Lien invalide</p>
            <p class="text-sm text-error-700 dark:text-error-300 mt-1">
              Ce lien de réinitialisation est invalide ou a expiré.
            </p>
          </div>
        </div>
      </div>

      <router-link
        to="/forgot-password"
        class="block w-full h-11 flex items-center justify-center rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white font-medium transition-all duration-200"
      >
        Nouvelle demande
      </router-link>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="handleSubmit" class="space-y-5">
      <!-- Error Alert -->
      <div
        v-if="error"
        class="p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800"
      >
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm text-error-700 dark:text-error-400">{{ error }}</p>
        </div>
      </div>

      <!-- Password -->
      <div>
        <label for="password" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
          Nouveau mot de passe
        </label>
        <div class="relative">
          <input
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            id="password"
            placeholder="••••••••"
            autocomplete="new-password"
            class="w-full h-11 px-4 pr-11 rounded-lg border border-corporate-300 bg-white text-corporate-900 placeholder:text-corporate-400 transition-all duration-150 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-corporate-400 hover:text-corporate-600"
          >
            <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
        <!-- Password Strength -->
        <div v-if="form.password" class="mt-2">
          <div class="flex gap-1">
            <div
              v-for="i in 5"
              :key="i"
              :class="['h-1 flex-1 rounded-full transition-colors', i <= passwordStrength.score ? passwordStrength.color : 'bg-corporate-200 dark:bg-corporate-700']"
            />
          </div>
          <p class="text-xs text-corporate-500 mt-1">{{ passwordStrength.label }}</p>
        </div>
      </div>

      <!-- Confirm Password -->
      <div>
        <label for="confirmPassword" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
          Confirmer le mot de passe
        </label>
        <input
          v-model="form.confirmPassword"
          type="password"
          id="confirmPassword"
          placeholder="••••••••"
          autocomplete="new-password"
          :class="[
            'w-full h-11 px-4 rounded-lg border bg-white text-corporate-900 placeholder:text-corporate-400 transition-all focus:outline-none focus:ring-2 dark:bg-corporate-900 dark:text-white',
            form.confirmPassword && form.password !== form.confirmPassword
              ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20'
              : 'border-corporate-300 focus:border-accent-500 focus:ring-accent-500/20 dark:border-corporate-700'
          ]"
        />
        <p v-if="form.confirmPassword && form.password !== form.confirmPassword" class="mt-1 text-xs text-error-500">
          Les mots de passe ne correspondent pas
        </p>
      </div>

      <!-- Password Requirements -->
      <div class="p-4 bg-corporate-50 dark:bg-corporate-800/50 rounded-lg">
        <p class="text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-2">
          Exigences :
        </p>
        <ul class="space-y-1 text-sm">
          <li :class="form.password.length >= 8 ? 'text-success-600' : 'text-corporate-500'">
            • Au moins 8 caractères
          </li>
          <li :class="/[A-Z]/.test(form.password) && /[a-z]/.test(form.password) ? 'text-success-600' : 'text-corporate-500'">
            • Majuscules et minuscules
          </li>
          <li :class="/[0-9]/.test(form.password) ? 'text-success-600' : 'text-corporate-500'">
            • Au moins un chiffre
          </li>
          <li :class="/[^a-zA-Z0-9]/.test(form.password) ? 'text-success-600' : 'text-corporate-500'">
            • Au moins un caractère spécial
          </li>
        </ul>
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full h-11 flex items-center justify-center gap-2 rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-corporate-400/50 dark:focus:ring-[#1E3A5F]/50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Réinitialiser le mot de passe
      </button>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AuthLayout } from '@/layouts'
import { useAuth, useToast } from '@/composables'

const route = useRoute()
const router = useRouter()
const { resetPassword } = useAuth()
const toast = useToast()

const token = computed(() => route.query.token as string)

const form = reactive({
  password: '',
  confirmPassword: ''
})

const error = ref('')
const showPassword = ref(false)
const isLoading = ref(false)

const passwordStrength = computed(() => {
  const password = form.password
  if (!password) return { score: 0, label: '', color: '' }

  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++

  const levels = [
    { label: 'Très faible', color: 'bg-error-500' },
    { label: 'Faible', color: 'bg-error-400' },
    { label: 'Moyen', color: 'bg-warning-500' },
    { label: 'Fort', color: 'bg-success-400' },
    { label: 'Très fort', color: 'bg-success-500' }
  ]

  return { score, ...levels[Math.min(score, 4)] }
})

const handleSubmit = async () => {
  error.value = ''
  isLoading.value = true

  if (!token.value) {
    error.value = 'Token de réinitialisation invalide'
    isLoading.value = false
    return
  }

  if (!form.password) {
    error.value = 'Le mot de passe est requis'
    isLoading.value = false
    return
  }

  if (form.password.length < 8) {
    error.value = 'Le mot de passe doit contenir au moins 8 caractères'
    isLoading.value = false
    return
  }

  if (form.password !== form.confirmPassword) {
    error.value = 'Les mots de passe ne correspondent pas'
    isLoading.value = false
    return
  }

  try {
    const result = await resetPassword(token.value, form.password)

    if (!result.success) {
      error.value = result.error || 'Erreur lors de la réinitialisation'
    } else {
      toast.success('Mot de passe réinitialisé', 'Vous pouvez maintenant vous connecter')
      router.push('/login')
    }
  } catch (e: any) {
    error.value = e.message || 'Une erreur est survenue'
  } finally {
    isLoading.value = false
  }
}
</script>
